/**
 * Navigation context provider
 *
 * Tracks navigation source for player detail pages and maintains a
 * breadcrumb stack for contextual navigation (SE-4025). The breadcrumb
 * stack is limited to 3 levels: section → player → similar player.
 * @module providers/NavigationProvider
 */

import { usePathname } from "expo-router";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

/** Possible navigation sources for player detail pages */
export type NavigationSource =
  | "kanban"
  | "direct"
  | "similar-players"
  | "squad-planning"
  | null;

/** A single breadcrumb entry in the navigation trail */
export interface BreadcrumbEntry {
  /** Display label (e.g. "All Targets", player name) */
  readonly label: string;
  /** Navigation path to return to this entry */
  readonly path: string;
  /**
   * When true, pressing this crumb also reopens the Global Search modal over
   * the destination tab in addition to navigating to `path` (SE-5387). Global
   * Search is a reactive-var overlay with no dedicated route, so returning to
   * it requires re-raising `globalSearchOpenVar`, not just a router navigation.
   * On small screens the modal isn't mounted, so this is a harmless no-op.
   */
  readonly reopenSearch?: boolean;
}

/** Maximum breadcrumb depth — section → player → similar player */
const MAX_BREADCRUMB_DEPTH = 3;

/** Context type for navigation state management */
interface NavigationContextType {
  readonly navigationSource: NavigationSource;
  readonly setNavigationSource: (source: NavigationSource) => void;
  readonly isKanbanNavigation: boolean;
  readonly isSimilarPlayersNavigation: boolean;
  /** Current breadcrumb trail for the player page header */
  readonly breadcrumbs: readonly BreadcrumbEntry[];
  /** Push a new entry onto the breadcrumb stack (max 3 levels) */
  readonly pushBreadcrumb: (entry: BreadcrumbEntry) => void;
  /**
   * Replace the last breadcrumb entry (SE-5369). Lets the page that owns the
   * current player reconcile a crumb that a caller seeded from stale/in-flight
   * data. No-op when the stack is empty.
   */
  readonly updateLastBreadcrumb: (entry: BreadcrumbEntry) => void;
  /** Pop the breadcrumb stack to a specific index (keeps entries 0..index) */
  readonly popToIndex: (index: number) => void;
  /** Clear the breadcrumb stack (e.g. when leaving player pages) */
  readonly clearBreadcrumbs: () => void;
}

const NavigationContext = createContext<NavigationContextType>({
  navigationSource: null,
  setNavigationSource: () => {},
  isKanbanNavigation: false,
  isSimilarPlayersNavigation: false,
  breadcrumbs: [],
  pushBreadcrumb: () => {},
  updateLastBreadcrumb: () => {},
  popToIndex: () => {},
  clearBreadcrumbs: () => {},
});

/**
 * Hook to access navigation context values
 * @returns Navigation context with source, breadcrumbs, and setters
 */
export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  return (
    context || {
      navigationSource: null,
      setNavigationSource: () => {},
      isKanbanNavigation: false,
      isSimilarPlayersNavigation: false,
      breadcrumbs: [],
      pushBreadcrumb: () => {},
      updateLastBreadcrumb: () => {},
      popToIndex: () => {},
      clearBreadcrumbs: () => {},
    }
  );
};

/** Props for NavigationProvider */
interface NavigationProviderProps {
  /** Child components that need navigation context */
  readonly children: React.ReactNode;
}

/**
 * Provides navigation source tracking and breadcrumb stack management
 * @param root0 - Provider props
 * @param root0.children - Child components
 */
export /**
 *
 */
const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const pathname = usePathname();
  const [navigationSource, setNavigationSourceState] =
    useState<NavigationSource>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<readonly BreadcrumbEntry[]>(
    []
  );

  const setNavigationSource = useCallback((source: NavigationSource) => {
    setNavigationSourceState(source);
  }, []);

  const [lastPathname, setLastPathname] = useState(pathname);

  const pushBreadcrumb = useCallback((entry: BreadcrumbEntry) => {
    setBreadcrumbs(prev => {
      if (prev.length < MAX_BREADCRUMB_DEPTH) return [...prev, entry];
      // SE-4613 E1: at max depth, PIN the left-most (origin section) crumb and
      // drop the MIDDLE entry instead of the origin. Otherwise a 3-deep
      // Similar-Player chain (origin → A → B, push C) would shift the origin
      // section out of the stack, so the left-most crumb on C would point at an
      // intermediate player rather than the originating section. Keep index 0,
      // drop index 1, keep the rest, append the new entry.
      return [prev[0], ...prev.slice(2), entry];
    });
  }, []);

  const updateLastBreadcrumb = useCallback((entry: BreadcrumbEntry) => {
    setBreadcrumbs(prev =>
      prev.length === 0 ? prev : [...prev.slice(0, -1), entry]
    );
  }, []);

  const popToIndex = useCallback((index: number) => {
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  }, []);

  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbs([]);
  }, []);

  const isKanbanNavigation = navigationSource === "kanban";
  const isSimilarPlayersNavigation = navigationSource === "similar-players";

  const value = useMemo(
    () => ({
      navigationSource,
      setNavigationSource,
      isKanbanNavigation,
      isSimilarPlayersNavigation,
      breadcrumbs,
      pushBreadcrumb,
      updateLastBreadcrumb,
      popToIndex,
      clearBreadcrumbs,
    }),
    [
      navigationSource,
      setNavigationSource,
      isKanbanNavigation,
      isSimilarPlayersNavigation,
      breadcrumbs,
      pushBreadcrumb,
      updateLastBreadcrumb,
      popToIndex,
      clearBreadcrumbs,
    ]
  );

  // The source describes the ENTRY POINT of the current /players/* sub-
  // navigation only. Entry points set it right before router.push (while the
  // pathname is still the origin section), so it is cleared only on pathname
  // CHANGES that land outside /players — otherwise a stale source would
  // mislabel later visits (SE-5350: Squad→player then Scouting→player would
  // still highlight Squad). Render-phase adjustment instead of an effect,
  // per the React "adjusting state when props change" guidance.
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    if (!pathname.startsWith("/players")) {
      setNavigationSourceState(null);
    }
  }

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};
